import { useRef } from 'react';
import { Check } from 'lucide-react';
import { Button } from '../new-components/ui/button';
import { TENANT } from '../../config';

const NET_WORTH_RANGES = [
  { value: 'less-50k', label: 'Less than $50k' },
  { value: '50k-100k', label: '$50k - $100k' },
  { value: '100k-200k', label: '$100k - $200k' },
  { value: '200k-500k', label: '$200k - $500k' },
  { value: '500k-1m', label: '$500k - $1M' },
  { value: '1m-5m', label: '$1M - $5M' },
  { value: 'over-5m', label: 'Over $5M' },
];

interface NetWorthStepProps {
  value: string;
  onSelect: (value: string) => void;
  onContinue: () => void;
}

export function NetWorthStep({ value, onSelect, onContinue }: NetWorthStepProps) {
  const isRedexBuild = String(process.env.REACT_APP_TENANT_ID || '').trim().toLowerCase() === 'redex';
  // Arriving with an answer already chosen means the user came back to this step. Show an
  // explicit Continue so they don't have to re-tap their saved choice. First-time visits keep
  // the tap-to-auto-advance momentum (no button captured at mount).
  const startedWithValue = useRef(value !== '');

  return (
    <div className="flex flex-col h-full">
      <div className="space-y-2 shrink-0">
        <h1 className="text-3xl md:text-4xl font-medium tracking-tight">
          What is your estimated net worth?
        </h1>
        <p className="text-base text-muted-foreground">
          {isRedexBuild
            ? `This helps ${TENANT.branding.name} tailor onboarding and compliance checks.`
            : 'We want to make the wealth building potential of owning rental homes & vacation rentals more accessible.'}
        </p>
      </div>

      {/* Options flex-fill the remaining height and scale between a min/max so all seven
          fit on screen without scrolling, regardless of viewport height. */}
      <div className="mt-5 flex-1 min-h-0 flex flex-col gap-2">
        {NET_WORTH_RANGES.map((range) => (
          <button
            key={range.value}
            onClick={() => onSelect(range.value)}
            className={`flex-1 min-h-[3rem] max-h-[4.5rem] w-full flex items-center justify-between px-5 rounded-xl border-2 transition-all duration-200 text-left active:scale-[0.99] motion-reduce:active:scale-100 ${
              value === range.value
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/30 bg-background'
            }`}
          >
            <span className="text-base font-medium">{range.label}</span>
            <Check
              className={`w-5 h-5 text-primary transition-all duration-200 ${
                value === range.value ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
              }`}
            />
          </button>
        ))}
      </div>

      {startedWithValue.current && value !== '' && (
        <div className="mt-4 shrink-0">
          <Button onClick={onContinue} className="w-full h-14 text-lg">
            Continue
          </Button>
        </div>
      )}
    </div>
  );
}
